/**
 * Reliquary House — Luxury Cart Controller
 * Handles AJAX cart updates, variant option resolution (Edition & Size),
 * selected-item price calculation, and direct permalink checkout.
 */

(function() {
  function initReliquaryCart() {
    const container = document.querySelector('[data-reliquary-cart-section]');
    if (!container) return;

    // Toast helper
    function showToast(message) {
      let toast = document.querySelector('.rel-cart-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'rel-cart-toast';
        toast.style.cssText = `
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          background: #111111;
          color: #FFFFFF;
          font-family: 'Inter', sans-serif;
          font-size: 0.8125rem;
          padding: 0.75rem 1.25rem;
          border-radius: 4px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.15);
          z-index: 9999;
          transition: opacity 0.3s ease, transform 0.3s ease;
          opacity: 0;
          transform: translateY(10px);
        `;
        document.body.appendChild(toast);
      }
      toast.textContent = message;
      toast.style.opacity = '1';
      toast.style.transform = 'translateY(0)';
      setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(10px)';
      }, 3000);
    }

    // Format currency in Indian Rupees format (or fallback)
    function formatMoneyCents(cents) {
      const amount = cents / 100;
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format(amount);
    }

    // Refresh Cart via AJAX
    function refreshCart() {
      container.classList.add('is-loading');
      fetch(window.location.pathname + window.location.search)
        .then(res => res.text())
        .then(htmlText => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(htmlText, 'text/html');
          const newSection = doc.querySelector('[data-reliquary-cart-section]');
          if (newSection) {
            container.innerHTML = newSection.innerHTML;
            bindEvents();
          }
          container.classList.remove('is-loading');
        })
        .catch(err => {
          console.error('[Reliquary Cart] Refresh failed:', err);
          window.location.reload();
        });
    }

    // Update Line Item Quantity via /cart/change.js
    function updateQuantity(lineKey, newQty) {
      container.classList.add('is-loading');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: parseInt(newQty, 10) })
      })
      .then(res => res.json())
      .then(() => {
        refreshCart();
      })
      .catch(err => {
        console.error('[Reliquary Cart] Quantity update failed:', err);
        refreshCart();
      });
    }

    // Update Line Item Variant via /cart/change.js + /cart/add.js
    function changeLineVariant(lineKey, currentQty, newVariantId) {
      container.classList.add('is-loading');
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      })
      .then(() => {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: newVariantId, quantity: parseInt(currentQty, 10) }] })
        });
      })
      .then(res => res.json())
      .then(() => {
        refreshCart();
      })
      .catch(err => {
        console.error('[Reliquary Cart] Variant change failed:', err);
        refreshCart();
      });
    }

    function bindEvents() {
      const selectAll = container.querySelector('[data-rel-select-all]');
      const itemCheckboxes = container.querySelectorAll('[data-rel-item-checkbox]');
      const selectedCounter = container.querySelector('[data-rel-selected-count]');
      const summaryMrpEl = container.querySelector('[data-rel-summary-mrp]');
      const summaryDiscountEl = container.querySelector('[data-rel-summary-discount]');
      const summaryDiscountRow = container.querySelector('[data-rel-summary-discount-row]');
      const summaryTotalEl = container.querySelector('[data-rel-summary-total]');
      const checkoutBtn = container.querySelector('[data-rel-checkout-btn]');
      const checkoutErrorEl = container.querySelector('[data-rel-checkout-error]');

      // Recalculate Price Details & Selection Counter based ONLY on checked items
      function updateSelectionAndPrices() {
        const totalItems = itemCheckboxes.length;
        const checkedBoxes = Array.from(itemCheckboxes).filter(cb => cb.checked);
        const selectedCount = checkedBoxes.length;

        if (selectedCounter) {
          selectedCounter.textContent = `${selectedCount} OF ${totalItems} ITEMS SELECTED`;
        }

        if (selectAll) {
          selectAll.checked = totalItems > 0 && selectedCount === totalItems;
        }

        let totalMrpCents = 0;
        let totalFinalCents = 0;

        checkedBoxes.forEach(cb => {
          const card = cb.closest('[data-cart-item-card]');
          if (!card) return;

          const priceCents = parseInt(card.getAttribute('data-item-price'), 10) || 0;
          const compareCents = parseInt(card.getAttribute('data-item-compare-price'), 10) || priceCents;
          const qtyInput = card.querySelector('[data-rel-qty-input]');
          const qty = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;

          totalMrpCents += compareCents * qty;
          totalFinalCents += priceCents * qty;
        });

        const discountCents = Math.max(0, totalMrpCents - totalFinalCents);

        if (summaryMrpEl) summaryMrpEl.textContent = formatMoneyCents(totalMrpCents);
        
        if (summaryDiscountEl) summaryDiscountEl.textContent = '- ' + formatMoneyCents(discountCents);
        if (summaryDiscountRow) {
          summaryDiscountRow.classList.toggle('hidden', discountCents <= 0);
        }

        if (summaryTotalEl) summaryTotalEl.textContent = formatMoneyCents(totalFinalCents);

        // Checkout Button State & Zero-Selection Validation
        if (checkoutBtn) {
          if (selectedCount === 0) {
            checkoutBtn.disabled = true;
            checkoutBtn.classList.add('is-disabled');
            if (checkoutErrorEl) checkoutErrorEl.classList.remove('hidden');
          } else {
            checkoutBtn.disabled = false;
            checkoutBtn.classList.remove('is-disabled');
            if (checkoutErrorEl) checkoutErrorEl.classList.add('hidden');
          }
        }
      }

      // 1. Select All Event
      if (selectAll) {
        selectAll.addEventListener('change', function() {
          itemCheckboxes.forEach(cb => {
            cb.checked = selectAll.checked;
          });
          updateSelectionAndPrices();
        });
      }

      // Individual Checkboxes
      itemCheckboxes.forEach(cb => {
        cb.addEventListener('change', updateSelectionAndPrices);
      });

      // Initial calculation
      updateSelectionAndPrices();

      // 2. Remove Selected Items
      const removeSelectedBtn = container.querySelector('[data-rel-remove-selected]');
      if (removeSelectedBtn) {
        removeSelectedBtn.addEventListener('click', function() {
          const checked = Array.from(itemCheckboxes).filter(cb => cb.checked);
          if (checked.length === 0) {
            showToast('Please select at least one item to remove.');
            return;
          }

          const updates = {};
          checked.forEach(cb => {
            const key = cb.getAttribute('data-line-key');
            if (key) updates[key] = 0;
          });

          container.classList.add('is-loading');
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates })
          })
          .then(() => refreshCart())
          .catch(() => refreshCart());
        });
      }

      // 3. Share Cart Button
      const shareBtn = container.querySelector('[data-rel-share-cart]');
      if (shareBtn) {
        shareBtn.addEventListener('click', function() {
          const shareUrl = window.location.href;
          if (navigator.clipboard) {
            navigator.clipboard.writeText(shareUrl).then(() => {
              showToast('Cart link copied to clipboard.');
            }).catch(() => {
              showToast('Share link: ' + shareUrl);
            });
          } else {
            showToast('Share link: ' + shareUrl);
          }
        });
      }

      // 4. Per-Item Quantity Controls (+ / - / Input)
      container.querySelectorAll('[data-rel-qty-btn]').forEach(btn => {
        btn.addEventListener('click', function() {
          const lineKey = this.getAttribute('data-line-key');
          const delta = parseInt(this.getAttribute('data-delta'), 10);
          const input = container.querySelector(`input[data-line-key="${lineKey}"]`);
          if (input) {
            const currentVal = parseInt(input.value, 10) || 1;
            const newQty = Math.max(0, currentVal + delta);
            updateQuantity(lineKey, newQty);
          }
        });
      });

      container.querySelectorAll('[data-rel-qty-input]').forEach(input => {
        input.addEventListener('change', function() {
          const lineKey = this.getAttribute('data-line-key');
          const newQty = Math.max(0, parseInt(this.value, 10) || 0);
          updateQuantity(lineKey, newQty);
        });
      });

      // 5. Per-Item Remove (X) Button
      container.querySelectorAll('[data-rel-remove-item]').forEach(btn => {
        btn.addEventListener('click', function() {
          const lineKey = this.getAttribute('data-line-key');
          updateQuantity(lineKey, 0);
        });
      });

      // 6. Dynamic Variant Selectors (Size & Edition)
      container.querySelectorAll('[data-rel-variant-selector]').forEach(select => {
        select.addEventListener('change', function() {
          const lineKey = this.getAttribute('data-line-key');
          const currentQty = this.getAttribute('data-current-qty');
          const itemCard = this.closest('[data-cart-item-card]');
          if (!itemCard) return;

          const sizeSelect = itemCard.querySelector('select[data-option-type="Artwork Size"], select[data-option-type="Size"]');
          const editionSelect = itemCard.querySelector('select[data-option-type="Edition"]');
          const variantsDataScript = itemCard.querySelector('script[data-product-variants]');

          if (!variantsDataScript) return;
          const variants = JSON.parse(variantsDataScript.textContent || '[]');

          const selectedSize = sizeSelect ? sizeSelect.value : null;
          const selectedEdition = editionSelect ? editionSelect.value : null;

          const matchedVariant = variants.find(v => {
            const optSizeMatches = !selectedSize || v.options.includes(selectedSize);
            const optEditionMatches = !selectedEdition || v.options.includes(selectedEdition);
            return optSizeMatches && optEditionMatches;
          });

          if (matchedVariant) {
            changeLineVariant(lineKey, currentQty, matchedVariant.id);
          } else {
            showToast('The selected combination is unavailable.');
            refreshCart();
          }
        });
      });

      // 7. Secure Checkout with ONLY Selected Items (Direct Permalink Checkout)
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', function(e) {
          e.preventDefault();

          const checkedBoxes = Array.from(container.querySelectorAll('[data-rel-item-checkbox]:checked'));
          if (checkedBoxes.length === 0) {
            if (checkoutErrorEl) checkoutErrorEl.classList.remove('hidden');
            showToast('Select at least one artwork to continue.');
            return;
          }

          const selectedItems = [];
          checkedBoxes.forEach(cb => {
            const card = cb.closest('[data-cart-item-card]');
            if (!card) return;
            const variantId = card.getAttribute('data-variant-id');
            const qtyInput = card.querySelector('[data-rel-qty-input]');
            const qty = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;
            if (variantId) {
              selectedItems.push(`${variantId}:${qty}`);
            }
          });

          if (selectedItems.length > 0) {
            const checkoutUrl = `/cart/${selectedItems.join(',')}`;
            window.location.href = checkoutUrl;
          }
        });
      }
    }

    bindEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReliquaryCart);
  } else {
    initReliquaryCart();
  }
})();
